// Allows using UI hashes defined in Marionette in a Stickit binding.
// Include this in a Marionette view in place of backbone.stickit. It will load and return backbone.stickit.
import { parseDate, formatDate } from './utils/dates'
import _ from 'underscore'
import Backbone from 'backbone'
import { View } from 'backbone.marionette'
// Ensure Stickit is loaded before patching it to work with Marionette
import Stickit from 'backbone.stickit'

// Initializes bootstrap-datepicker on $el with our default options
const initializeDatepicker = function ($el) {
  // Will only run the first time before datepicker is initialized
  $el.datepicker({
    // Allow 2 digit years to be considered recent ones as expected.
    // By default, any year more than 10 years in the future is considered to be in the 1900s.
    assumeNearbyYear: true,
    // Close the widget once a date is clicked on
    autoclose: true,
    // Disable keyboard on mobile devices since it takes up half the screen and conflicts with the datepicker
    disableTouchKeyboard: true,
    enableOnReadonly: false
  })
}

// Add handler for bootstrap-datepicker inputs.
// This assumes that the view, which utilizes a bootstrap-datepicker requires the library in its dependencies.
Stickit.addHandler({
  selector: '.datepicker',
  initialize: initializeDatepicker,
  // Adds keyup to default list so that after the date is parsed, we'll get the date again to update the model.
  // This fixes model updating in Safari when the date is inputted via the keyboard vs via clicking in the UI.
  // Before the keyup fix, we'd often see today's date or the wrong date persisted in Safari because datepicker
  // would keep trying to parse the date on every character input except the last one (so it'd often be parsing
  // an invalid date and ending up with today instead).
  events: ['input', 'change', 'keyup'],
  destroy($el) {
    $el.datepicker('destroy')
  },
  getVal($el) {
    const date = $el.datepicker('getDate')
    // Persist SQL date if one is defined
    return date ? formatDate(date, 'sqlDate') : date
  },
  update($el, val) {
    // `update` is called before initialize if `updateView` is true (usually the case), so we need to initialize
    // first in order to set the datepicker params. Params can only be set on the first call to `$el.datepicker()`.
    // See http://nytimes.github.io/backbone.stickit/#binding-callbacks-flowchart for the callback flow.
    initializeDatepicker($el)
    if (val) {
      // val from model should be in ISO / SQL date format (YYYY-MM-DD)
      $el.datepicker('setDate', parseDate(val))
    } else {
      $el.datepicker('clearDates')
    }
  }
})

// Stickit selectors can be hashes, strings, or undefined.  We need to normalize each
// type against the provided ui hash, which itself might be undefined.

function normalizeSelector(selector, ui) {
  // Compatible with Marionette v3+ only
  // Calling directly on View.prototype ensures that this works correctly in the callees
  if (_.isObject(selector)) {
    return View.prototype.normalizeUIKeys(selector, ui)
  } else if (_.isString(selector)) {
    return View.prototype.normalizeUIString(selector, ui)
  }
  return selector
}

// Shim the three standard Stickit calls into Marionette View objects so that we
// can use the @ui syntax for bindings; this eliminates repetition of possibly long
// and complex selectors and allows us to have one definition for them, in the ui
// hash or function for the View.

_.extend(View.prototype, {
  stickit(optionalModel, optionalBindings) {
    const bindings = optionalBindings || _.result(this, 'bindings') || {}

    return Backbone.View.prototype.stickit.call(
      this,
      optionalModel,
      this.normalizeUIKeys(bindings)
    )
  },

  addBinding(optionalModel, selector, configuration) {
    const ui = _.result(this, 'ui')
    const uiBindings = _.result(this, '_uiBindings')
    const uiSelector = normalizeSelector(selector, uiBindings || ui)

    return Backbone.View.prototype.addBinding.call(
      this,
      optionalModel,
      uiSelector,
      configuration
    )
  },

  unstickit(optionalModel, optionalSelector) {
    const ui = _.result(this, 'ui')
    const uiBindings = _.result(this, '_uiBindings')
    const uiSelector = normalizeSelector(optionalSelector, uiBindings || ui)

    return Backbone.View.prototype.unstickit.call(
      this,
      optionalModel,
      uiSelector
    )
  }
})

export default Stickit
